/* ==========================================================================
   Product page: on small screens the price slides into the header (left slot)
   once the in-page price has scrolled out of view.
   ========================================================================== */
(() => {
  const mq = window.matchMedia('(max-width: 1099px)');
  let observer = null;
  let priceEl = null;
  let badge = null;

  const currentPriceText = () => {
    const sale = document.querySelector('.product__info-container .price-item--last, .product__info-container .price-item--sale');
    const regular = document.querySelector('.product__info-container .price-item--regular');
    const el = sale && sale.textContent.trim() ? sale : regular;
    return el ? el.textContent.trim().replace(/\s+/g, ' ') : '';
  };

  const ensureBadge = () => {
    if (badge) return badge;
    const left = document.querySelector('.ag-header__left');
    if (!left) return null;
    badge = document.createElement('span');
    badge.className = 'ag-header__price';
    left.appendChild(badge);
    return badge;
  };

  const observe = () => {
    if (observer) observer.disconnect();
    priceEl = document.querySelector('.product__info-container .price');
    if (!priceEl || !mq.matches) {
      document.body.classList.remove('ag-price-in-header');
      return;
    }
    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ag-header-height'), 10) || 50;
    observer = new IntersectionObserver(
      ([entry]) => {
        const above = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
        if (above) {
          const b = ensureBadge();
          if (b) b.textContent = currentPriceText();
        }
        document.body.classList.toggle('ag-price-in-header', above);
      },
      { rootMargin: `-${headerHeight}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(priceEl);
  };

  const init = () => {
    observe();
    mq.addEventListener('change', observe);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Dawn re-renders the price block when a variant changes
  document.addEventListener('variant:change', () => window.setTimeout(observe, 300));
})();

/* ==========================================================================
   Full-screen image viewer: vertical thumbnail strip (bottom-left) that tracks
   the image in view and scrolls to an image when clicked.
   ========================================================================== */
(() => {
  const setup = (modal) => {
    if (modal.dataset.agThumbs === 'true') return;
    const content = modal.querySelector('.product-media-modal__content');
    if (!content) return;
    const imgOf = (el) => (el.tagName === 'IMG' ? el : el.querySelector('img'));
    const items = Array.from(content.children).filter((el) => imgOf(el));
    if (items.length < 2) return;
    modal.dataset.agThumbs = 'true';

    const list = document.createElement('ul');
    list.className = 'ag-viewer-thumbs';
    list.setAttribute('role', 'list');
    const buttons = items.map((item, index) => {
      const img = imgOf(item);
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.setAttribute('aria-label', `Image ${index + 1} of ${items.length}`);
      const thumb = document.createElement('img');
      const src = img.currentSrc || img.src;
      thumb.src = src.replace(/([?&])width=\d+/, '$1width=120');
      thumb.alt = '';
      thumb.loading = 'lazy';
      button.appendChild(thumb);
      button.addEventListener('click', () => item.scrollIntoView({ behavior: 'smooth', block: 'start' }));
      li.appendChild(button);
      list.appendChild(li);
      return button;
    });
    modal.querySelector('.product-media-modal__dialog').appendChild(list);

    const setActive = (index) => buttons.forEach((b, i) => b.classList.toggle('is-active', i === index));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(items.indexOf(entry.target));
        });
      },
      { root: content, rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    items.forEach((item) => observer.observe(item));
    setActive(0);
  };

  const init = () => {
    document.querySelectorAll('product-modal').forEach((modal) => {
      new MutationObserver(() => {
        if (modal.hasAttribute('open')) setup(modal);
      }).observe(modal, { attributes: true, attributeFilter: ['open'] });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* Size guide pop-up: native <dialog>, closes on the X, Escape or a click on the dimmed backdrop */
(() => {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-ag-size-guide-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function') dialog.showModal();
      return;
    }
    if (event.target.closest('[data-ag-size-guide-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target.matches('dialog.ag-size-guide__dialog')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });
})();

/* ==========================================================================
   Pinned gallery (desktop): the counter on the pinned image follows the image
   scrolling beside it, and a "+" opens the viewer on that image.
   ========================================================================== */
(() => {
  const mq = window.matchMedia('(min-width: 750px)');

  const setup = (gallery) => {
    const list = gallery.querySelector('.product__media-list');
    if (!list || gallery.dataset.agPinned === 'true') return;
    const items = Array.from(list.querySelectorAll(':scope > .product__media-item'));
    if (items.length < 2) return;
    gallery.dataset.agPinned = 'true';

    const counterNumber = items[0].querySelector('.ag-media-counter')?.firstChild;
    const pad = (value) => String(value).padStart(2, '0');
    let current = items[1];

    const setCurrent = (item) => {
      current = item;
      if (counterNumber) counterNumber.nodeValue = pad(items.indexOf(item) + 1);
    };

    const tracker = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && mq.matches) setCurrent(entry.target);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );
    items.slice(1).forEach((item) => tracker.observe(item));
    if (mq.matches) setCurrent(items[1]);

    const zoom = document.createElement('button');
    zoom.type = 'button';
    zoom.className = 'ag-gallery-zoom';
    zoom.setAttribute('aria-label', 'Zoom image');
    zoom.innerHTML =
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" aria-hidden="true"><path d="M12 3v18M3 12h18"/></svg>';
    zoom.addEventListener('click', () => current.querySelector('.product__modal-opener button, .product__media-toggle')?.click());
    gallery.appendChild(zoom);

    new IntersectionObserver(([entry]) => zoom.classList.toggle('is-visible', entry.isIntersecting), {
      rootMargin: '-20% 0px -60% 0px',
    }).observe(list);
  };

  const init = () => document.querySelectorAll('product-info media-gallery').forEach(setup);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
